/**
 * Vendor sign-up — the public front door to the brand-claim flow.
 *
 * Runs outside the admin shell: whoever lands here has no session. The form's
 * whole job is to tell a rep which path they are on before they spend time
 * signing up — claim an unclaimed brand, register a brand we do not carry, ask
 * about an archived one, or learn the brand is already spoken for.
 *
 * Nothing is lodged from this page. A claim is recorded against a user and an
 * organization, so every outcome hands off to registration; the claim itself is
 * submitted from the authenticated flow once the rep has an account.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, ButtonBase, CircularProgress } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { BrandMatchOutcome, ProofKind, vendorSignupApi } from '../../services/vendorSignupApi';
import { bvColor, bvRadius, bvShadow, bvTransition, bvType } from './brandVendorsTokens';
import { BvButton } from './components/BrandVendorsPrimitives';

const SAMPLE_BRANDS = ['Canon', 'Sigma', 'Aurora Optics'];

const PROOFS: Array<{ key: ProofKind; label: string; note: string; turnaround: string }> = [
  {
    key: 'domain',
    label: 'Work email domain',
    note: 'We match your email domain against the brand’s website. Fastest when they line up.',
    turnaround: 'Minutes',
  },
  {
    key: 'letter',
    label: 'Authorisation letter',
    note: 'A signed letter on brand letterhead naming you as an authorised representative.',
    turnaround: '1–2 days',
  },
  {
    key: 'dns',
    label: 'DNS TXT record',
    note: 'Place a token we give you on the brand’s domain. Strongest proof of control.',
    turnaround: 'Same day',
  },
];

/**
 * Outcome-specific copy and colouring for the second card.
 *
 * Every outcome funnels to the same next step: create an account and an
 * organization, then submit the claim from there. Claims are recorded against a
 * user and an org, so there is no way to lodge one from a session-less form —
 * this page's job is to tell the rep which path they are on before they sign up.
 */
const OUTCOME_COPY: Record<
  BrandMatchOutcome,
  { status: string; title: string; note: string; ink: string; border: string; bg: string; cta: string; footnote: string }
> = {
  unclaimed: {
    status: 'matched',
    title: 'We carry this brand and nobody has claimed it',
    note: 'Create an account and you can submit the claim with one proof that you represent the brand. An admin reviews it and, once approved, your organization owns the brand record.',
    ink: bvColor.success,
    border: bvColor.successBorder,
    bg: bvColor.surface,
    cta: 'Create an account to claim it',
    footnote: 'Approval transfers ownership and grants MAP publishing on this brand’s catalog.',
  },
  new: {
    status: 'new brand',
    title: 'This brand is not in the catalog yet',
    note: 'Create an account and we will add the brand record from your request. The proof burden is lighter because there is no existing catalog or owner at stake.',
    ink: bvColor.warning,
    border: bvColor.warningBorder,
    bg: bvColor.surface,
    cta: 'Create an account to add it',
    footnote: 'A new brand starts at the Registered stage with no products until ingestion is configured.',
  },
  inactive: {
    status: 'archived',
    title: 'We already hold this brand, but it is archived',
    note: 'An admin has to reactivate the existing record. Creating a second one would split its catalog and history, so this path goes to a person rather than straight into the queue.',
    ink: bvColor.warning,
    border: bvColor.warningBorder,
    bg: bvColor.surface,
    cta: 'Create an account to ask an admin',
    footnote: 'Reactivating keeps the brand’s existing products and ingestion history.',
  },
  claimed: {
    status: 'already claimed',
    title: 'This brand is already claimed',
    note: 'Another organization already owns this brand record. We will not transfer it automatically — an admin mediates, and the existing owner keeps the claim unless they release it.',
    ink: bvColor.danger,
    border: bvColor.dangerBorder,
    bg: bvColor.dangerBg,
    cta: 'Create an account to contest it',
    footnote: 'We never show you who the current owner is; an admin mediates any transfer.',
  },
};

export const VendorSignupPage: React.FC = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [debounced, setDebounced] = useState('');
  const [proof, setProof] = useState<ProofKind>('domain');

  // Debounce so a keystroke does not fire a lookup per character.
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(name.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [name]);

  const { data, isFetching, error } = useQuery({
    queryKey: ['vendor-signup-brand-match', debounced],
    queryFn: () => vendorSignupApi.matchBrand(debounced),
    enabled: debounced.length > 1,
  });

  const outcome: BrandMatchOutcome | null = debounced.length > 1 && data ? data.outcome : null;
  const copy = useMemo(() => (outcome ? OUTCOME_COPY[outcome] : null), [outcome]);

  const borderColor = !outcome ? bvColor.border : copy!.border;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: bvColor.borderSubtle,
        color: bvColor.ink3,
      }}
    >
      {/* slim public bar — no admin shell on this route */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          height: 52,
          px: '22px',
          bgcolor: bvColor.surface,
          borderBottom: `1px solid ${bvColor.border}`,
        }}
      >
        <Box component="img" src="/logo-black.svg" alt="Motzu Labs" sx={{ height: 20, width: 'auto' }} />
        <Box sx={{ ...bvType.caption, color: bvColor.muted }}>Public sign-up — no admin session</Box>
      </Box>

      <Box sx={{ maxWidth: 600, mx: 'auto', px: '20px', py: '28px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {/* brand card */}
        <Box
          sx={{
            bgcolor: bvColor.surface,
            border: `1px solid ${bvColor.border}`,
            borderRadius: `${bvRadius.card}px`,
            boxShadow: bvShadow.card,
            p: '16px',
          }}
        >
          <Box component="h1" sx={{ ...bvType.signupTitle, color: bvColor.ink, m: 0 }}>
            Which brand do you represent?
          </Box>
          <Box sx={{ ...bvType.caption, color: bvColor.muted, mt: '5px', lineHeight: 1.5 }}>
            We check the name against the brands we already carry, so you get the right path straight away.
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              mt: '13px',
              height: 40,
              px: '12px',
              border: `1px solid ${borderColor}`,
              borderRadius: `${bvRadius.control}px`,
              bgcolor: bvColor.surface,
              transition: bvTransition,
              '&:focus-within': { borderColor: outcome ? borderColor : bvColor.accent },
            }}
          >
            <Box
              component="input"
              aria-label="Brand name"
              value={name}
              placeholder="Brand name"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setName(event.target.value)}
              sx={{
                flex: 1,
                minWidth: 0,
                border: 'none',
                outline: 'none',
                bgcolor: 'transparent',
                color: bvColor.ink,
                ...bvType.body,
                '&::placeholder': { color: bvColor.muted },
              }}
            />
            {isFetching ? (
              <CircularProgress size={13} sx={{ color: bvColor.muted }} />
            ) : (
              copy && (
                <Box sx={{ ...bvType.caption, fontWeight: 500, color: copy.ink, whiteSpace: 'nowrap' }}>
                  {copy.status}
                </Box>
              )
            )}
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', mt: '11px' }}>
            {SAMPLE_BRANDS.map((sample) => (
              <ButtonBase
                key={sample}
                onClick={() => setName(sample)}
                sx={{
                  height: 27,
                  px: '11px',
                  borderRadius: `${bvRadius.pill}px`,
                  border: `1px solid ${bvColor.border}`,
                  color: bvColor.ink3,
                  transition: bvTransition,
                  ...bvType.caption,
                  '&:hover': { borderColor: bvColor.accent, color: bvColor.accentHover },
                  '&:focus-visible': { outline: `2px solid ${bvColor.accent}`, outlineOffset: 1 },
                }}
              >
                {sample}
              </ButtonBase>
            ))}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: '11px' }}>
              {(error as Error).message}
            </Alert>
          )}
        </Box>

        {/* outcome card */}
        {outcome && copy && (
          <Box
            sx={{
              bgcolor: bvColor.surface,
              border: `1px solid ${copy.border}`,
              borderRadius: `${bvRadius.card}px`,
              boxShadow: bvShadow.card,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ bgcolor: copy.bg, borderBottom: `1px solid ${copy.border}`, p: '16px' }}>
              <Box sx={{ ...bvType.cardTitle, color: copy.ink }}>{copy.title}</Box>
              <Box sx={{ ...bvType.caption, color: bvColor.ink3, mt: '5px', lineHeight: 1.5 }}>{copy.note}</Box>
            </Box>

            {/* proof picker — only where a claim is actually possible */}
            {outcome === 'unclaimed' && (
              <Box sx={{ p: '16px' }}>
                <Box sx={{ ...bvType.eyebrow, mb: '9px' }}>Pick one proof</Box>
                <Box role="radiogroup" aria-label="Proof of representation" sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {PROOFS.map((option) => {
                    const on = proof === option.key;
                    return (
                      <ButtonBase
                        key={option.key}
                        role="radio"
                        aria-checked={on}
                        onClick={() => setProof(option.key)}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '11px',
                          textAlign: 'left',
                          p: '11px',
                          borderRadius: `${bvRadius.panel}px`,
                          border: `1px solid ${on ? bvColor.accent : bvColor.border}`,
                          bgcolor: on ? bvColor.surfaceTint : bvColor.surface,
                          transition: bvTransition,
                          '&:hover': { borderColor: on ? bvColor.accent : bvColor.accentTint },
                          '&:focus-visible': { outline: `2px solid ${bvColor.accent}`, outlineOffset: 1 },
                        }}
                      >
                        <Box
                          aria-hidden
                          sx={{
                            width: 15,
                            height: 15,
                            mt: '2px',
                            flexShrink: 0,
                            borderRadius: '50%',
                            border: `1px solid ${on ? bvColor.accent : bvColor.muted2}`,
                            display: 'grid',
                            placeItems: 'center',
                          }}
                        >
                          {on && <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: bvColor.accent }} />}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ ...bvType.tableSecondary, fontWeight: 500, color: bvColor.ink }}>{option.label}</Box>
                          <Box sx={{ ...bvType.microCaption, color: bvColor.muted, mt: '3px', lineHeight: 1.45 }}>
                            {option.note}
                          </Box>
                        </Box>
                        <Box sx={{ ...bvType.microCaption, color: bvColor.muted, whiteSpace: 'nowrap', mt: '2px' }}>
                          {option.turnaround}
                        </Box>
                      </ButtonBase>
                    );
                  })}
                </Box>
              </Box>
            )}

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                bgcolor: bvColor.page,
                borderTop: `1px solid ${bvColor.borderSubtle}`,
                px: '16px',
                py: '13px',
              }}
            >
              <BvButton
                size="lg"
                tone="primary"
                // A claim is recorded against a user and an organization, so the
                // rep has to have an account before one can be lodged. Hand off
                // to registration rather than pretending to submit here.
                onClick={() =>
                  navigate(
                    `/signup?intent=claim-brand&brand=${encodeURIComponent(data?.brandName || debounced)}`
                  )
                }
              >
                {copy.cta}
              </BvButton>
              <Box sx={{ ...bvType.microCaption, color: bvColor.muted, textAlign: 'right', flex: 1, lineHeight: 1.45 }}>
                {copy.footnote}
              </Box>
            </Box>
          </Box>
        )}

        <Box sx={{ ...bvType.microCaption, color: bvColor.muted, lineHeight: 1.5, px: '2px' }}>
          Nothing is submitted from this page. Claims are recorded against an account and an organization, so the next
          step is signing up — sign-up can then add a brand we do not have yet, but it can never claim, rename or
          duplicate a brand we already manage without an admin approving it.
        </Box>
      </Box>
    </Box>
  );
};

export default VendorSignupPage;
